/**
 * Auth Provider
 *
 * Integrates Clerk authentication with the SDK.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  ClerkProvider,
  useAuth as useClerkAuth,
  useUser as useClerkUser,
} from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { userKeys } from '../hooks/user-keys';
import { createUserService, type UserProfile } from '../services/user.service';
import { useApiClient } from './SdkProvider';
import type { TonnieClient } from '../client/base';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface AuthContextValue {
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string | null;
  user: AuthUser | null;
  token: string | null;
  signOut: () => Promise<void>;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName: string;
  imageUrl?: string;
  role: string;
  tenantId?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════

export const AuthContext = createContext<AuthContextValue | null>(null);

// ═══════════════════════════════════════════════════════════════════════
// AUTH SYNC COMPONENT
// ═══════════════════════════════════════════════════════════════════════

type ClerkUserResource = ReturnType<typeof useClerkUser>['user'];

/**
 * Keep the browser's copy of the Clerk user in step with the account name.
 *
 * A name change is written through Clerk and the platform's mirror together
 * (platform-api `AccountNameService`), then the profile query refetches — but
 * the Clerk user this page loaded keeps the old name until Clerk's own next
 * refresh, so the header and every greeting read from it stayed stale. When the
 * profile (the mirror) names someone the loaded Clerk user does not, reload it
 * — once per new name, so a mirror that lags behind Clerk can never loop.
 *
 * It only OBSERVES the profile query: `enabled: false`, so the pages that show
 * the profile fetch it and this never adds a request of its own. It still
 * carries the REAL queryFn. It used `skipToken`, and a query refetched by an
 * invalidation runs the options its observers last set — whenever this
 * provider re-rendered after the page's `useUserProfile`, an invalidation (a
 * name change, a linked account, a new photo) ran `skipToken` instead:
 * "Attempted to invoke queryFn when set to skipToken", and no refetch — the
 * profile stayed stale (cold run 3, 29 Sep, business-web).
 */
function useAccountNameInStep(clerkUser: ClerkUserResource, client: TonnieClient) {
  const { data: profile } = useQuery<UserProfile>({
    queryKey: userKeys.profile(),
    queryFn: () => createUserService(client).getProfile(),
    enabled: false,
  });
  const reloadedFor = useRef<string | null>(null);
  const mirror = profile ? `${profile.firstName ?? ''}\u0000${profile.lastName ?? ''}` : null;
  const shown = clerkUser ? `${clerkUser.firstName ?? ''}\u0000${clerkUser.lastName ?? ''}` : null;
  useEffect(() => {
    if (!clerkUser || mirror === null || mirror === shown || reloadedFor.current === mirror) return;
    reloadedFor.current = mirror;
    // A failed reload leaves the name as it was until Clerk's next refresh —
    // nothing to recover here, and nothing the person must act on.
    clerkUser.reload().catch(() => undefined);
  }, [clerkUser, mirror, shown]);
}

function AuthSync({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId, getToken, signOut } = useClerkAuth();
  const { user: clerkUser } = useClerkUser();
  const client = useApiClient();
  const [token, setToken] = useState<string | null>(null);

  // Newest Clerk auth state, readable from the (stable) token provider below
  // without re-installing it every time `getToken`'s identity changes.
  const authRef = useRef({ isSignedIn, getToken });
  authRef.current = { isSignedIn, getToken };

  // ⚠️ Installed during RENDER — deliberately NOT inside an effect.
  //
  // React flushes CHILD effects before PARENT effects. On a fresh load the page
  // mounts before Clerk has loaded; when Clerk settles, `isSignedIn` flips in
  // one commit and the page's queries start from their own effects BEFORE this
  // component's effects run. With the provider installed in an effect, those
  // requests left with no `Authorization` header and the API answered 401 —
  // which is how "Accept invitation" stayed disabled on every reload of
  // /accept-invite in production (HotFix 2026.9.15). Any query gated only on
  // `isSignedIn` was exposed; hooks that also waited for `token` were not.
  //
  // citizen-mobile's `ClerkAuthSync` has installed its provider this way since
  // `3b074903c`; this is the same fix for the web apps.
  //
  // Safe at render time: `client` is a stable singleton and the install is
  // guarded, so a StrictMode double-render is a no-op. The provider fetches a
  // fresh Clerk JWT before every request (≈60 s TTL) and returns null when
  // signed out, so no request carries a session after sign-out.
  const installedOn = useRef<unknown>(null);
  if (installedOn.current !== client) {
    installedOn.current = client;
    client.setTokenProvider(async () => {
      const { isSignedIn: signedIn, getToken: read } = authRef.current;
      return signedIn ? read() : null;
    });
  }

  // `token` is still exposed for consumers that gate on it.
  useEffect(() => {
    if (isSignedIn) {
      getToken().then((t) => {
        setToken(t);
        client.setAuthToken(t);
      });
    } else {
      setToken(null);
      client.setAuthToken(null);
    }
  }, [isSignedIn, getToken, client]);

  useAccountNameInStep(clerkUser, client);

  // Map Clerk user to our format
  const user: AuthUser | null = clerkUser
    ? {
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || '',
        firstName: clerkUser.firstName,
        lastName: clerkUser.lastName,
        fullName:
          clerkUser.fullName || `${clerkUser.firstName || ''} ${clerkUser.lastName || ''}`.trim(),
        imageUrl: clerkUser.imageUrl,
        role: (clerkUser.publicMetadata?.['role'] as string) || 'citizen',
        tenantId: clerkUser.publicMetadata?.['tenantId'] as string | undefined,
      }
    : null;

  const handleSignOut = useCallback(async () => {
    await signOut();
    client.setAuthToken(null);
  }, [signOut, client]);

  const value: AuthContextValue = {
    isLoaded,
    isSignedIn: isSignedIn ?? false,
    userId: userId ?? null,
    user,
    token,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ═══════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════

export interface AuthProviderProps {
  children: ReactNode;
  publishableKey: string;
  afterSignInUrl?: string;
  afterSignUpUrl?: string;
  signInUrl?: string;
  signUpUrl?: string;
  /**
   * Clerk satellite-domain configuration.
   *
   * When Tonnie runs multiple portals under one Clerk instance — e.g.
   * `citizen.tonnie.ai` (primary) + `business.tonnie.ai` (satellite) —
   * the satellite app must set `isSatellite` and `domain` so Clerk can
   * perform the cross-origin session handshake. Without this, an identity
   * signed in on the primary hits the "You're already signed in" error
   * when submitting the sign-in form on the satellite.
   *
   * See https://clerk.com/docs/advanced-usage/satellite-domains
   *
   * Leave both unset for the primary app.
   */
  isSatellite?: boolean;
  /** The satellite's own domain (e.g. `business.tonnie.ai`). Required when `isSatellite` is true. */
  domain?: string;
}

export function AuthProvider({
  children,
  publishableKey,
  afterSignInUrl = '/',
  afterSignUpUrl = '/onboarding',
  signInUrl = '/login',
  signUpUrl = '/signup',
  isSatellite,
  domain,
}: AuthProviderProps): ReactNode {
  // `isSatellite` + `domain` are only passed through when the caller opts in.
  // Clerk's ClerkProvider treats `undefined` as "not a satellite", so this
  // stays backward-compatible for journey-web (primary) which doesn't set them.
  const satelliteProps = isSatellite && domain ? ({ isSatellite: true, domain } as const) : {};

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signInUrl={signInUrl}
      signUpUrl={signUpUrl}
      signInFallbackRedirectUrl={afterSignInUrl}
      signUpFallbackRedirectUrl={afterSignUpUrl}
      {...satelliteProps}
    >
      <AuthSync>{children}</AuthSync>
    </ClerkProvider>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useUser(): AuthUser | null {
  const { user } = useAuth();
  return user;
}

export function useIsAuthenticated(): boolean {
  const { isSignedIn, isLoaded } = useAuth();
  return isLoaded && isSignedIn;
}

export function useRequireAuth(): AuthUser {
  const { user, isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    throw new Error('Auth not loaded');
  }

  if (!isSignedIn || !user) {
    throw new Error('User not authenticated');
  }

  return user;
}
